import type { Activity, PlanDay } from "../types";

/** Whole calendar days from `from` to `to`, both "YYYY-MM-DD". */
export function daysBetween(from: string, to: string): number {
  const [fy, fm, fd] = from.split("-").map(Number);
  const [ty, tm, td] = to.split("-").map(Number);
  return Math.round((Date.UTC(ty, tm - 1, td) - Date.UTC(fy, fm - 1, fd)) / 86_400_000);
}

/** The calendar day `days` before or after a "YYYY-MM-DD" date. */
export function shiftDay(day: string, days: number): string {
  const [y, m, d] = day.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}

/**
 * The earlier day a family may still want to record, if there is one.
 *
 * Production showed families opening the day and almost never ticking it: most
 * of the work happens away from the screen, often from the printed plan. The
 * curriculum only moves on days with a tick, so a child whose parent never came
 * back to tick stayed on the first day of the first term.
 *
 * So the home page asks once about the most recent earlier day — and only that
 * one. Reaching further back turns a gentle question into a backlog, and a
 * family that has been away for a week should start from today, not from a
 * list of what they missed.
 *
 * Returns null when that day is older than `maxAgeDays`, or when every core
 * activity on it is already done. Optional extras never make a day look
 * unfinished, the same rule the progress ring follows.
 */
export function catchUpDay(
  plans: PlanDay[],
  activities: Activity[],
  today: string,
  maxAgeDays = 3,
): PlanDay | null {
  const latest = plans
    .filter((plan) => plan.plan_date < today)
    .sort((a, b) => b.plan_date.localeCompare(a.plan_date))[0];
  if (!latest) return null;

  const age = daysBetween(latest.plan_date, today);
  if (age < 1 || age > maxAgeDays) return null;

  const byId = new Map(activities.map((activity) => [activity.id, activity]));
  const pendingCore = latest.items.some((item) => {
    const activity = byId.get(item.activity_id);
    return activity && activity.track !== "enrichment" && item.status !== "done";
  });
  return pendingCore ? latest : null;
}

/** Midday on a plan's own date, so a late tick is filed on the day it happened. */
export function middayOn(day: string): string {
  const [y, m, d] = day.split("-").map(Number);
  return new Date(y, m - 1, d, 12, 0, 0).toISOString();
}
